import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { EMPTY, Observable, defer, fromEvent, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';

/**
 * BrowserService
 *
 * SSR-safe abstraction for browser-specific APIs.
 * Encapsulates direct access to window, document, and other browser globals
 * to ensure compatibility with server-side rendering.
 *
 * Features:
 * - Safe access to document.visibilityState
 * - URL.createObjectURL/revokeObjectURL abstraction
 * - Audio playback abstraction
 * - Platform detection utilities
 *
 * @example
 * ```typescript
 * constructor(private browserService: BrowserService) {}
 *
 * // Check visibility
 * if (this.browserService.isDocumentVisible()) {
 *   this.refresh();
 * }
 *
 * // Create object URL
 * const url = this.browserService.createObjectURL(blob);
 * // ... use url
 * this.browserService.revokeObjectURL(url);
 *
 * // Play notification sound
 * this.browserService.playAudio('/assets/sounds/notification.mp3', 0.3);
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class BrowserService {
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Check if running in a browser environment
   */
  isPlatformBrowser(): boolean {
    return this.isBrowser;
  }

  /**
   * Get document visibility state
   * @returns 'visible' | 'hidden' | 'prerender' or null if not in browser
   */
  getVisibilityState(): DocumentVisibilityState | null {
    if (!this.isBrowser) {
      return null;
    }
    return this.document.visibilityState;
  }

  /**
   * Check if document is currently visible
   * @returns true if visible, false otherwise (including SSR)
   */
  isDocumentVisible(): boolean {
    if (!this.isBrowser) {
      return false;
    }
    return this.document.visibilityState === 'visible';
  }

  /**
   * Create an object URL for a blob
   * @param blob - The blob to create a URL for
   * @returns The object URL, or null if not in browser
   */
  createObjectURL(blob: Blob): string | null {
    if (!this.isBrowser) {
      return null;
    }
    return URL.createObjectURL(blob);
  }

  /**
   * Revoke an object URL to free memory
   * @param url - The object URL to revoke
   */
  revokeObjectURL(url: string): void {
    if (this.isBrowser && url) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Play an audio file
   * @param src - Path to the audio file
   * @param volume - Volume level (0.0 to 1.0), defaults to 1.0
   * @returns Promise that resolves when playback starts, or null if not in browser
   */
  playAudio(src: string, volume = 1.0): Promise<void> | null {
    if (!this.isBrowser) {
      return null;
    }

    try {
      const audio = new Audio(src);
      audio.volume = Math.max(0, Math.min(1, volume)); // Clamp volume between 0 and 1
      return audio.play().catch(() => {
        // Ignore audio playback errors (e.g., autoplay blocked)
      });
    } catch {
      // Ignore if Audio constructor is not available
      return null;
    }
  }

  /**
   * Get the window object safely
   * @returns Window object or null if not in browser
   */
  getWindow(): Window | null {
    if (!this.isBrowser) {
      return null;
    }
    return this.document.defaultView;
  }

  /**
   * Get the document object safely
   * @returns Document object or null if not in browser
   */
  getDocument(): Document | null {
    if (!this.isBrowser) {
      return null;
    }
    return this.document;
  }

  /**
   * RF-QA-171 / RF-QA-172 · A polling clock that only ticks while the tab is
   * visible. Emits `0` immediately and then once every `periodMs` while
   * `document.visibilityState` is not `hidden`; pauses while the tab is hidden
   * and, on becoming visible again, refreshes at once when the last tick is at
   * least one period old — otherwise it waits out the remainder of the period,
   * so rapid tab switching never bursts the API. Outside a browser (SSR) it
   * degrades to a plain `timer(0, periodMs)`. Every header poll (health,
   * trading-health, exchange credentials) is built on this so a backgrounded
   * tab stops hitting the API.
   */
  visibleTimer(periodMs: number): Observable<number> {
    if (!this.isBrowser) {
      return timer(0, periodMs);
    }
    // Per-subscription state: when this subscriber last received a tick.
    return defer(() => {
      let lastTickAt = Number.NEGATIVE_INFINITY;
      return fromEvent(this.document, 'visibilitychange').pipe(
        map(() => this.document.visibilityState !== 'hidden'),
        startWith(this.document.visibilityState !== 'hidden'),
        distinctUntilChanged(),
        switchMap((visible) => {
          if (!visible) {
            return EMPTY;
          }
          const sinceLastTick = Date.now() - lastTickAt;
          return timer(sinceLastTick >= periodMs ? 0 : periodMs - sinceLastTick, periodMs);
        }),
        tap(() => {
          lastTickAt = Date.now();
        }),
      );
    });
  }

  /**
   * Add a visibility change listener
   * @param callback - Callback to invoke when visibility changes
   * @returns Cleanup function to remove the listener, or null if not in browser
   */
  onVisibilityChange(callback: (state: DocumentVisibilityState) => void): (() => void) | null {
    if (!this.isBrowser) {
      return null;
    }

    const handler = () => callback(this.document.visibilityState);
    this.document.addEventListener('visibilitychange', handler);

    return () => {
      this.document.removeEventListener('visibilitychange', handler);
    };
  }
}
